import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { SERVICE_AREAS } from "../data/site";

const COORDS = {
  "paulden": [34.8856, -112.4682],
  "chino-valley": [34.7575, -112.4538],
  "williamson-valley": [34.6610, -112.5840],
  "prescott": [34.5400, -112.4685],
  "prescott-valley": [34.6100, -112.3157],
  "dewey": [34.5800, -112.2440],
  "humboldt": [34.5010, -112.2340],
  "cordes-junction": [34.3300, -112.1000],
};

const TERRITORY = [
  COORDS["paulden"],
  COORDS["williamson-valley"],
  COORDS["prescott"],
  COORDS["cordes-junction"],
  COORDS["humboldt"],
  COORDS["dewey"],
  COORDS["prescott-valley"],
  COORDS["chino-valley"],
];

const LABEL_DIRECTION = {
  "paulden": "top",
  "chino-valley": "top",
  "williamson-valley": "left",
  "prescott": "left",
  "prescott-valley": "top",
  "dewey": "right",
  "humboldt": "right",
  "cordes-junction": "right",
};

export default function ServiceAreaMap() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return undefined;
    const map = L.map(containerRef.current, {
      scrollWheelZoom: false,
      zoomControl: true,
      attributionControl: true,
    }).setView([34.56, -112.33], 9);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 16,
    }).addTo(map);

    L.polygon(TERRITORY, {
      color: "#0284C7",
      weight: 2,
      dashArray: "7 7",
      fillColor: "#0284C7",
      fillOpacity: 0.06,
    }).addTo(map);

    SERVICE_AREAS.forEach((area) => {
      const marker = L.marker(COORDS[area.slug], {
        title: `${area.name} — dumpster service area`,
        keyboard: true,
        icon: L.divIcon({
          className: "yws-marker-wrap",
          html: `<span class="yws-marker" data-testid="map-area-${area.slug}" role="link" aria-label="Dumpster service in ${area.name}"></span>`,
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        }),
      }).addTo(map);
      const dir = LABEL_DIRECTION[area.slug] || "top";
      const offset = dir === "top" ? [0, -14] : dir === "left" ? [-12, 0] : [12, 0];
      marker.bindTooltip(area.name, { permanent: true, direction: dir, offset, className: "yws-tooltip", interactive: false });
      marker.on("click", () => navigate(`/service-areas/${area.slug}`));
    });

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [navigate]);

  return (
    <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-lg bg-slate-100" data-testid="service-area-map">
      <div ref={containerRef} className="h-[360px] sm:h-[440px] w-full" aria-label="Interactive map of YWS service areas in Yavapai County" />
      <ul className="sr-only">
        {SERVICE_AREAS.map((a) => (
          <li key={a.slug}><a href={`/service-areas/${a.slug}`}>{a.name}</a></li>
        ))}
      </ul>
    </div>
  );
}
